import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const PER_GROUP_LIMIT = 6;

const PAGES = [
  { label: 'Home', path: '/' },
  { label: 'Connections', path: '/connections' },
  { label: 'Events', path: '/events' },
  { label: 'Generate magic link', path: '/connections?modal=magic-link' },
  { label: 'Webhooks', path: '/webhooks' },
  { label: 'API Log', path: '/api-log' },
  { label: 'Data Explorer (Sandbox)', path: '/data-explorer' },
  { label: 'Data Explorer (Production)', path: '/data-explorer?mode=production' },
  { label: 'Integrations', path: '/integrations' },
  { label: 'Settings', path: '/settings' },
  { label: 'Users', path: '/settings/users' },
  { label: 'Invite user', path: '/settings/users?modal=invite-user', adminOnly: true },
  { label: 'API Keys', path: '/settings/api-keys' },
  { label: 'Create API key', path: '/settings/api-keys?modal=create-api-key' },
  { label: 'Connection Settings', path: '/settings/connection-settings' },
  { label: 'Branding', path: '/settings/branding' },
  { label: 'Magic Links', path: '/settings/magic-links' },
  { label: 'Notifications', path: '/settings/notifications' },
  { label: 'Usage & Billing', path: '/settings/usage-billing', adminOnly: true },
  { label: 'Documentation', href: 'https://docs.trackstarhq.com' },
];

// startsWith beats a mid-string hit, so an exact-ish match floats to the top
function rank(haystacks, q) {
  let best = Infinity;
  haystacks.forEach((h) => {
    const idx = h.indexOf(q);
    if (idx === -1) return;
    const score = idx === 0 ? 0 : 1;
    if (score < best) best = score;
  });
  return best;
}

// bold the slice of text that matched the query; plain text when it doesn't
function highlight(text, q) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-blue-500">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}

function CommandPalette({ connections, integrationLists }) {
  const navigate = useNavigate();
  const { orgRole } = useAuth();
  const isAdmin = orgRole === 'admin';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setQuery('');
        setActiveIndex(0);
        setOpen((prev) => !prev);
      }
    };
    const handleOpen = () => {
      setQuery('');
      setActiveIndex(0);
      setOpen(true);
    };
    document.addEventListener('keydown', handleKey);
    window.addEventListener('pokecenter:open-search', handleOpen);
    return () => {
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('pokecenter:open-search', handleOpen);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const flatIntegrations = useMemo(() => {
    const out = [];
    if (!integrationLists) return out;
    Object.keys(integrationLists).forEach((type) => {
      const byName = integrationLists[type] || {};
      Object.keys(byName).forEach((name) => {
        if (name === 'sandbox') return;
        const displayName = byName[name].display_name || name;
        out.push({ type, name, displayName });
      });
    });
    return out;
  }, [integrationLists]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const pageHits = [];
    PAGES.forEach((page) => {
      // hide admin-only destinations from non-admins, matching the app's gates
      if (page.adminOnly && !isAdmin) return;
      const score = rank([page.label.toLowerCase()], q);
      if (score === Infinity) return;
      pageHits.push({
        score,
        r: {
          kind: 'page', key: page.path || page.href, label: page.label, path: page.path, href: page.href,
        },
      });
    });
    pageHits.sort((a, b) => a.score - b.score);

    const connHits = [];
    (connections || []).forEach((conn) => {
      const type = conn.integration_type;
      const byName = (integrationLists && integrationLists[type]) || {};
      const entry = byName[conn.integration_name];
      const displayName = (entry && entry.display_name) || conn.integration_name;
      const id = (conn.connection_id || '').toLowerCase();
      const customerId = (conn.customer_id || '').toLowerCase();
      const fields = [
        displayName.toLowerCase(), conn.integration_name.toLowerCase(), id, customerId,
      ];
      const score = rank(fields, q);
      if (score === Infinity) return;
      connHits.push({
        score,
        r: {
          kind: 'connection',
          key: conn.connection_id,
          displayName,
          connectionId: conn.connection_id,
          customerId: conn.customer_id || null,
        },
      });
    });
    connHits.sort((a, b) => a.score - b.score);

    const intHits = [];
    flatIntegrations.forEach((int) => {
      const score = rank([int.displayName.toLowerCase(), int.name.toLowerCase()], q);
      if (score === Infinity) return;
      intHits.push({
        score,
        r: {
          kind: 'integration', key: `${int.type}/${int.name}`, displayName: int.displayName, type: int.type, name: int.name,
        },
      });
    });
    intHits.sort((a, b) => a.score - b.score);

    const out = [];
    pageHits.slice(0, PER_GROUP_LIMIT).forEach((h) => out.push(h.r));
    connHits.slice(0, PER_GROUP_LIMIT).forEach((h) => out.push(h.r));
    intHits.slice(0, PER_GROUP_LIMIT).forEach((h) => out.push(h.r));
    return out;
  }, [query, connections, integrationLists, flatIntegrations, isAdmin]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const select = (r) => {
    close();
    if (r.kind === 'page') {
      if (r.href) {
        window.open(r.href, '_blank', 'noopener');
      } else {
        navigate(r.path);
      }
    } else if (r.kind === 'connection') {
      navigate(`/connections?id=${r.connectionId}`);
    } else if (r.kind === 'integration') {
      navigate(`/integrations/${r.type}/${r.name}`);
    }
  };

  // indices where a new group starts (first row, and every kind change)
  const sectionStarts = () => {
    const starts = [];
    results.forEach((r, i) => {
      if (i === 0 || r.kind !== results[i - 1].kind) starts.push(i);
    });
    return starts;
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Tab') {
      // jump to the next (shift: previous) group's first row
      e.preventDefault();
      const starts = sectionStarts();
      if (starts.length <= 1) return;
      let currentStart = 0;
      starts.forEach((s) => { if (s <= activeIndex) currentStart = s; });
      const pos = starts.indexOf(currentStart);
      const nextPos = e.shiftKey
        ? (pos - 1 + starts.length) % starts.length
        : (pos + 1) % starts.length;
      setActiveIndex(starts[nextPos]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = results[activeIndex];
      if (chosen) select(chosen);
    }
  };

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const firstConnIdx = results.findIndex((r) => r.kind === 'connection');
  const firstIntIdx = results.findIndex((r) => r.kind === 'integration');
  const loadingConnections = connections === null || connections === undefined;
  const sectionCount = sectionStarts().length;

  // portal to body + high z-index so it sits above MUI Drawers/modals (~1300)
  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-start justify-center pt-[15vh]">
      <div
        className="fixed inset-0 bg-surface-900/50 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />
      <div className="bg-surface-0 rounded-xl shadow-xl w-[560px] max-w-[90vw] relative z-10 overflow-hidden">
        <div className="flex items-center gap-3 px-4 border-b border-surface-200">
          <SearchIcon sx={{ fontSize: 20 }} className="text-surface-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onKeyDown}
            placeholder="Search connections, integrations, or jump to a page…"
            className="flex-1 bg-transparent py-4 text-sm text-surface-900 outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-surface-400"
          />
          <kbd className="text-[10px] text-surface-400 border border-surface-200 rounded px-1.5 py-0.5">esc</kbd>
        </div>

        <div ref={listRef} className="max-h-80 overflow-y-auto py-1">
          {query.trim() === '' && (
            <p className="px-4 py-6 text-sm text-surface-400 text-center">Start typing to search.</p>
          )}
          {query.trim() !== '' && results.length === 0 && (
            <p className="px-4 py-6 text-sm text-surface-400 text-center">
              {loadingConnections ? 'Loading…' : 'No matches.'}
            </p>
          )}
          {results.map((r, idx) => {
            const isActive = idx === activeIndex;
            return (
              <div key={`${r.kind}-${r.key}`}>
                {idx === 0 && r.kind === 'page' && (
                  <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-surface-400">Go to</p>
                )}
                {idx === firstConnIdx && (
                  <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-surface-400">Connections</p>
                )}
                {idx === firstIntIdx && (
                  <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-surface-400">Integrations</p>
                )}
                <button
                  type="button"
                  data-idx={idx}
                  onClick={() => select(r)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${isActive ? 'bg-accent-50' : ''}`}
                >
                  {r.kind === 'page' && (r.href
                    ? <OpenInNewIcon sx={{ fontSize: 16 }} className="text-surface-400 shrink-0" />
                    : <DescriptionOutlinedIcon sx={{ fontSize: 16 }} className="text-surface-400 shrink-0" />)}
                  {r.kind === 'connection' && (
                    <LinkIcon sx={{ fontSize: 16 }} className="text-surface-400 shrink-0" />
                  )}
                  {r.kind === 'integration' && (
                    <ExtensionOutlinedIcon sx={{ fontSize: 16 }} className="text-surface-400 shrink-0" />
                  )}

                  {r.kind === 'page' && (
                    <span className="text-sm text-surface-900 truncate">{highlight(r.label, q)}</span>
                  )}
                  {r.kind === 'connection' && (
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-surface-900 truncate">{r.displayName}</span>
                      <span className="block text-xs font-mono text-surface-500 truncate">{highlight(r.connectionId, q)}</span>
                    </span>
                  )}
                  {r.kind === 'connection' && r.customerId && (
                    <span className="shrink-0 text-right leading-tight">
                      <span className="block text-[10px] uppercase tracking-wide text-surface-400">Customer ID</span>
                      <span className="block text-xs font-mono text-surface-700 truncate max-w-[10rem]">{highlight(r.customerId, q)}</span>
                    </span>
                  )}
                  {r.kind === 'integration' && (
                    <span className="text-sm text-surface-900 truncate">{highlight(r.displayName, q)}</span>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {results.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 border-t border-surface-200 bg-surface-50 text-[11px] text-surface-500">
            <span className="flex items-center gap-1.5">
              <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 border border-surface-200 rounded bg-surface-0">↓</kbd>
              <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 border border-surface-200 rounded bg-surface-0">↑</kbd>
              Navigate
            </span>
            {sectionCount > 1 && (
              <>
                <span className="text-surface-300">|</span>
                <span className="flex items-center gap-1.5">
                  <kbd className="inline-flex items-center justify-center h-[18px] px-1.5 border border-surface-200 rounded bg-surface-0">tab</kbd>
                  Switch section
                </span>
              </>
            )}
            <span className="ml-auto flex items-center gap-1.5">
              <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 border border-surface-200 rounded bg-surface-0">↵</kbd>
              Open
            </span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default CommandPalette;
